import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { aboutInfo } from '@/mocks/comics';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "关于女神漫画 - 品牌介绍与联系我们",
    "description": "女神漫画成立于2022年，是国内领先的免费全集漫画在线阅读平台。了解我们的品牌故事、核心价值观和发展历程。",
    "url": "https://nvshenmanhua.com/about",
    "inLanguage": "zh-Hans",
    "isPartOf": {
      "@type": "WebSite",
      "name": "女神漫画",
      "url": "https://nvshenmanhua.com"
    },
    "about": {
      "@type": "Organization",
      "name": "女神漫画",
      "description": "免费全集漫画在线阅读平台",
      "foundingDate": "2022",
      "url": "https://nvshenmanhua.com",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": "contact@nvshenmanhua.com",
        "telephone": "400-888-6666",
        "availableLanguage": ["Chinese"]
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "望京SOHO T3-A座 2501室",
        "addressLocality": "北京",
        "addressRegion": "朝阳区",
        "addressCountry": "CN"
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "首页",
          "item": "https://nvshenmanhua.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "关于我们",
          "item": "https://nvshenmanhua.com/about"
        }
      ]
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();

    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复您！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const formBody = new URLSearchParams();
      formData.forEach((value, key) => {
        formBody.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d9on5oosenbniolesi30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          setFormMessage('消息已发送，我们会尽快回复您！');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '发送失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络后重试');
    } finally {
      setFormStatus((prev) => (prev === 'loading' ? 'idle' : prev));
    }
  };

  return (
    <>
      <SEOHead
        title="关于女神漫画 - 品牌介绍与联系我们 | 免费全集漫画在线阅读"
        description="女神漫画成立于2022年，是国内领先的免费全集漫画在线阅读平台。了解品牌故事、使命愿景、核心价值观、发展历程、用户口碑及联系方式。"
        keywords="女神漫画,关于我们,品牌介绍,漫画在线阅读,免费全集,联系我们,公司介绍"
        canonicalUrl="https://nvshenmanhua.com/about"
        schemaJson={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">关于女神漫画</h1>
          </div>
        </div>

        <section className="py-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-14">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <p className="text-foreground-600 text-lg leading-relaxed max-w-3xl mx-auto">
                {aboutInfo.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-14">
              <div className="p-8 rounded-xl bg-background-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-4">
                  <i className="ri-focus-3-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
              </div>
              <div className="p-8 rounded-xl bg-background-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-eye-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
              </div>
            </div>

            <div className="mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
                核心价值观
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {aboutInfo.values.map((val) => (
                  <div key={val.title} className="p-6 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors">
                    <h3 className="text-base font-heading font-bold text-foreground-900 mb-2">{val.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{val.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 -translate-x-1/2" />
                <div className="space-y-8">
                  {aboutInfo.milestones.map((milestone, idx) => (
                    <div key={milestone.year} className={`relative flex items-center gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="hidden md:flex flex-1" />
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-4 border-background-50 -translate-x-1/2 z-10" />
                      <div className="flex-1 ml-10 md:ml-0 p-5 rounded-xl bg-background-100">
                        <span className="text-lg font-heading font-bold text-primary-600">{milestone.year}</span>
                        <p className="text-sm text-foreground-600 mt-1">{milestone.event}</p>
                      </div>
                      {idx % 2 === 0 ? <div className="hidden md:block flex-1" /> : <div className="hidden md:block flex-1" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-14">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {aboutInfo.testimonials.map((t) => (
                  <div key={t.name} className="p-6 rounded-xl bg-background-100">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i
                          key={i}
                          className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 text-center mb-8">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">联系电话</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-xl bg-background-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">公司地址</p>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100">
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-1">发送消息</h3>
                  <p className="text-sm text-foreground-500 mb-5">有任何问题或建议，欢迎随时联系我们</p>
                  <form
                    className="space-y-4"
                    data-readdy-form
                    onSubmit={handleSubmit}
                  >
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="sp-hp"
                    />
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的消息（最多500字）"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-3 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      ></textarea>
                    </div>
                    {formStatus === 'success' && (
                      <p className="text-sm text-accent-600 font-medium">{formMessage}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-primary-600 font-medium">{formMessage}</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full px-6 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <div className="mt-14 p-8 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-center">
              <h2 className="text-2xl font-heading font-bold text-background-50 mb-3">
                加入女神漫画，发现更多精彩
              </h2>
              <p className="text-background-50/80 mb-6 max-w-lg mx-auto">
                海量漫画资源，每日更新，我们等你来！
              </p>
              <Link
                to="/"
                className="inline-flex px-8 py-3 rounded-full bg-background-50 text-primary-600 font-medium text-sm hover:bg-background-100 transition-all cursor-pointer"
              >
                返回首页开始阅读
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}